// Documents resource — the write path behind the Document Uploads exceptions
// hub. The hub itself lists the `set filevine project id` gate TASKS (see
// lib/tasks.ts) and renders each gate's reason from its Review→Verdict exactly
// like the case verify gates; this file owns only the resolve action: a PUT
// assigning the correct Filevine case (matched_project_id, written through to
// the document's document_metadata).
//
// The document lifecycle still belongs to the workflow engine; this never
// changes task state directly. Completing the gate that assignment answers is
// the API's half of the same operation (TMF-371) — not a second call from here,
// which is how the two used to come apart.

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { reportActionError } from "~/lib/actionErrors";
import { halFetch, type HalResource } from "~/lib/hal";
import { TASKS_LIST_KEY } from "~/lib/tasks";

/** A document, as returned by GET /api/documents/:id and the PUT that assigns
 *  its Filevine case. `preview_url` is a short-TTL presigned GET minted per-View
 *  (never stored) so the review card can embed the file (TMF-227); absent when
 *  the document has no stored bytes. */
export type Document = HalResource<{
  id: string;
  upload_project_id: string;
  type: string;
  name: string;
  path: string;
  preview_url?: string | null;
}>;

/** Invalidation root for every documents cache entry. */
export const DOCUMENTS_KEY = ["documents"] as const;

export const documentQueryKey = (id: string) => [...DOCUMENTS_KEY, id] as const;

/** One document with its transient preview_url — GET /api/documents/:id. The
 *  review card loads this to embed the document the human is approving. */
export const documentQueryOptions = (id: string) =>
  queryOptions<Document>({
    queryKey: documentQueryKey(id),
    queryFn: ({ signal }) => halFetch<Document>(`/api/documents/${id}`, { signal }),
  });

/** useDocument fetches one document (with its short-TTL preview_url) for the
 *  review card. The request skips when the id is missing, or when `enabled` is
 *  explicitly false; the review card fetches for every row (historical rows
 *  included) so the reviewed document is always previewable. */
export function useDocument(id: string | undefined, enabled = true) {
  return useQuery({
    ...documentQueryOptions(id ?? ""),
    enabled: enabled && id !== undefined && id !== "",
  });
}

interface ResolveExceptionVariables {
  /** The flagged document (from the gate task's _embedded.document). */
  documentId: string;
  matchedProjectId: number;
}

/**
 * Assign the correct Filevine case to a flagged document — ONE request. The API
 * writes matched_project_id and completes the human `set filevine project id`
 * gate in the same operation (TMF-371).
 *
 * NOT a transaction: the API's writes are sequential and nothing rolls the
 * assignment back. What it guarantees is that everything able to refuse refuses
 * BEFORE any write, and that a late failure stays re-runnable — the assignment
 * lands with the gate still open, which is the state the review pane renders
 * from, so the document stays in the queue and resolving again finishes the job.
 *
 * It used to be two calls from here, and the second one failing (a roleless
 * user's event POST 400'd after the PUT had landed) left the document matched
 * but caseless forever: the engine never saw the gate answered. The SPA still
 * reports only what the human did; the engine owns all lifecycle state.
 */
export function resolveException({
  documentId,
  matchedProjectId,
}: ResolveExceptionVariables): Promise<Document> {
  return halFetch<Document>(`/api/documents/${documentId}`, {
    method: "PUT",
    body: { matched_project_id: matchedProjectId },
  });
}

/** resolveException as a mutation, invalidating the tasks + documents caches the
 *  resolved gate changes. */
export function useResolveException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resolveException,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TASKS_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY });
    },
    // The one request carries both halves, so it is refused for either — a
    // Filevine id the PUT won't take, or the gate completion the API won't
    // record. Reported here rather than at each call site so the review queue
    // and the task table both surface it (TMF-362).
    onError: (error) => reportActionError(error),
  });
}
